import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { FALLBACK_PRODUCTS, DEFAULT_PRODUCT_IMAGE } from "@/lib/brand-data";
import type { FallbackProduct } from "@/lib/brand-data";
import { LazyImage } from "@/components/ui/LazyImage";
import { ff } from "@/lib/feature-flags";
import { Skeleton } from "@/components/ui/Skeleton";

const BrandCards = () => {
  const { data: catalogData, isLoading } = useQuery({
    queryKey: ["catalog-showcase"],
    queryFn: () => api.catalog.products({ limit: "12" }),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Show skeleton placeholders while loading
  if (isLoading) {
    return (
      <section id="brands" className="py-10 bg-background overflow-hidden">
        <div className="container-max px-5 sm:px-8 lg:px-12 mb-8">
          <div className="text-center">
            <span className="text-xs font-bold text-secondary uppercase tracking-[0.15em]">
              Notre Gamme
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mt-2">
              Les produits que nous distribuons
            </h2>
          </div>
        </div>
        <div className="flex gap-3 sm:gap-4 md:gap-6 lg:gap-8 items-center justify-center px-8">
          {Array.from({ length: 6 }).map((_, i) => (
            ff.skeletonScreens ? (
              <div key={i} className="flex flex-col items-center gap-2">
                <Skeleton className="w-20 h-20 md:w-28 md:h-28 rounded-xl" />
                <Skeleton className="h-3 w-16 rounded" />
              </div>
            ) : (
              <div key={i} className="flex flex-col items-center gap-2 animate-pulse">
                <div className="w-20 h-20 md:w-28 md:h-28 rounded-xl bg-muted" />
                <div className="h-3 w-16 rounded bg-muted" />
              </div>
            )
          ))}
        </div>
      </section>
    );
  }

  const apiProducts: FallbackProduct[] =
    catalogData?.products?.map((p) => ({
      id: p.id,
      name: p.name,
      brand: p.brand?.replace("_", " ") ?? "",
      image: p.image_url || DEFAULT_PRODUCT_IMAGE,
    })) ?? [];

  const products = apiProducts.length > 0 ? apiProducts : FALLBACK_PRODUCTS;

  // Triple the list for smooth infinite scroll
  const items = [...products, ...products, ...products];

  return (
    <section id="brands" className="py-10 bg-background overflow-hidden">
      <div className="container-max px-5 sm:px-8 lg:px-12 mb-8">
        <div className="text-center">
          <span className="text-xs font-bold text-secondary uppercase tracking-[0.15em]">
            Notre Gamme
          </span>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mt-2">
            Les produits que nous distribuons
          </h2>
        </div>
      </div>

      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

        <div className="marquee-track flex gap-3 sm:gap-4 md:gap-6 lg:gap-8 items-center whitespace-nowrap">
          {items.map((product, i) => (
            <div
              key={`${product.id}-${i}`}
              className="flex-shrink-0 flex flex-col items-center gap-2 group"
            >
              <div className="w-20 h-20 md:w-28 md:h-28 rounded-xl overflow-hidden bg-muted/60 border border-border shadow-sm group-hover:shadow-md group-hover:border-secondary/30 transition-all duration-300">
                <LazyImage
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  skeletonClassName="w-20 h-20 md:w-28 md:h-28 rounded-xl"
                />
              </div>
              <span className="text-xs md:text-sm text-muted-foreground/70 font-medium text-center whitespace-nowrap max-w-[80px] md:max-w-[120px] truncate">
                {product.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrandCards;
